import { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../CustomSelect";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useTrainerSearch from "../hooks/useTrainerSearch";
import useRicercaSearch from "../hooks/useRicercaSearch";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Candidature = () => {
  const [candidature, setCandidature] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedTrainerOption, setSelectedTrainerOption] = useState(null);
  const [selectedRicercaOption, setSelectedRicercaOption] = useState(null);
  const [ricerche, setRicerche] = useState([]);
  const [trainerCodiceFiscale, setTrainerCodiceFiscale] = useState("");
  const [ricerchaId, setRicerchaId] = useState("");
  const userRole = localStorage.getItem("userRole");
  const loadTrainerOptions = useTrainerSearch();
  const loadRicercaOptions = useRicercaSearch();
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const fetchCandidature = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      try {
        setLoading(true);

        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const trainerId = selectedTrainerOption?.trainer?.id;

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (trainerId) {
          params.trainerId = trainerId;
        }

        if (ricerchaId) {
          params.ricercaId = ricerchaId;
        }

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get("/candidature", { params });

        if (response.status === 200) {
          const { content = [], page: pageInfo } = response.data ?? {};
          
          const formattedData = content.map((item) => ({
            ...item,
            dataCandidatura: formatDateOnly(item.dataCandidatura),
          }));
          setCandidature(formattedData);
          setTotalRows(pageInfo?.totalElements ?? content.length);
        } else {
          toast.error("Errore durante il recupero delle candidature!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          });
        }
      } catch (error) {
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      pageSize,
      debouncedSorting,
      debouncedColumnFilters,
      columnFilterFns,
      selectedTrainerOption,
      ricerchaId,
    ]
  );

  const fetchRicerche = async () => {
    try {
      setLoading(true);
      const response = await api.get("/ricerche");
      setRicerche(response.data.content || []);
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === "TRAINER") {
      alert(
        "Accedi come amministratore o operatore per visualizzare le posizioni aperte!"
      );
      navigate("/login");
      localStorage.clear();
    } else if (userRole === "ADMIN") {
      fetchRicerche();
    }
  }, [navigate, userRole]);

  useEffect(() => {
    if (userRole === "TRAINER") {
      return;
    }

    fetchCandidature();
  }, [userRole, fetchCandidature]);

  const handlePageChange = (newPage) => {
    setPage(Math.max(newPage - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleClearFilters = () => {
    setTrainerCodiceFiscale("");
    setSelectedTrainerOption(null);
    setSelectedRicercaOption(null);
    setRicerchaId("");
    setPage(0);
  };

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/candidature", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `candidature.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const columns = [
    { field: "nominativoDocente", header: "Nominativo Docente" },
    {
      field: "dataCandidatura",
      header: "Data Candidatura",
    },
    { field: "autoreCandidatura", header: "Autore Candidatura" },
    { field: "esito", header: "Esito" },
    { field: "titoloModulo", header: "Titolo Modulo" },
    { field: "sedeIndirizzo", header: "Sede Indirizzo" },
  ];

  const handleRowClick = (candidature) => {
    navigate(`/candidature/${candidature.id}`);
  };

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Candidature</div>
        <div className="controls">
          {userRole === "ADMIN" && (
            <div className="download-menu" ref={downloadRef}>
              <button
                className="download-btn"
                onClick={() => setIsDownloadOpen((prev) => !prev)}
              >
                Scarica
              </button>
              {isDownloadOpen && (
                <div className="download-dropdown">
                  <button onClick={() => handleExport("csv")}>CSV</button>
                  <button onClick={() => handleExport("xlsx")}>Excel</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="page-content candidature">
        {userRole === "ADMIN" && (
          <div className="filters-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fa-solid fa-filter"></i>
                Filtri di Ricerca
              </div>
              {(trainerCodiceFiscale || ricerchaId) && (
                <button
                  className="clear-filters-btn"
                  onClick={handleClearFilters}
                >
                  <i className="fa-solid fa-times"></i>
                  Cancella Filtri
                </button>
              )}
            </div>

            <div className="card-content">
              <div className="filters-grid">
                <div className="filter-field">
                  <label className="filter-label">
                    <i className="fa-solid fa-user"></i>
                    Docente
                  </label>
                  <div className="filter-input">
                    <CustomSelect
                      loadOptions={loadTrainerOptions}
                      value={selectedTrainerOption}
                      onChange={(selected) => {
                        setSelectedTrainerOption(selected);
                        const codiceFiscale =
                          selected?.trainer?.codiceFiscale ||
                          selected?.value ||
                          "";
                        setTrainerCodiceFiscale(codiceFiscale);
                      }}
                      placeholder="Seleziona un docente"
                      className="filter-select"
                    />
                  </div>
                </div>

                <div className="filter-field">
                  <label className="filter-label">
                    <i className="fa-solid fa-search"></i>
                    Ricerca
                  </label>
                  <div className="filter-input">
                    <CustomSelect
                      loadOptions={loadRicercaOptions}
                      defaultOptions={ricerche.map((ricerca) => ({
                        value: ricerca.id,
                        label: ricerca.titoloCorso ?? "",
                        ricerca,
                      }))}
                      value={selectedRicercaOption}
                      onChange={(selected) => {
                        setSelectedRicercaOption(selected);
                        setRicerchaId(selected?.ricerca?.id || selected?.value || "");
                      }}
                      placeholder="Seleziona una ricerca"
                      className="filter-select"
                    />
                  </div>
                </div>
              </div>

              <div className="filters-summary">
                {(trainerCodiceFiscale || ricerchaId) && (
                  <div className="active-filters">
                    {trainerCodiceFiscale && (
                      <span className="filter-tag">
                        <i className="fa-solid fa-user"></i>
                        Docente:{" "}
                        {selectedTrainerOption?.trainer?.nome || ""}{" "}
                        {selectedTrainerOption?.trainer?.cognome || ""}
                      </span>
                    )}
                    {ricerchaId && (
                      <span className="filter-tag">
                        <i className="fa-solid fa-search"></i>
                        Ricerca:{" "}
                        {selectedRicercaOption?.ricerca?.titoloCorso ||
                          selectedRicercaOption?.label ||
                          ricerchaId}
                      </span>
                    )}
                  </div>
                )}
                <div className="results-count">
                  {totalRows} candidature trovate
                </div>
              </div>
            </div>
          </div>
        )}

        <Table
          columns={columns}
          data={candidature}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default Candidature;
